import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { Check, X, Trash2, Plus, BarChart3, Users, Calendar, DollarSign, Activity, Save, Settings as SettingsIcon, KeyRound, Copy, FileText, AlertTriangle } from "lucide-react";
import TableControls, { applySort, applyFilters } from "@/components/TableControls";

export default function Admin() {
    const { user } = useAuth();
    const isSuper = user?.role === "super_admin";
    const [tab, setTab] = useState("overview");

    return (
        <div className="px-6 md:px-12 lg:px-16 py-10 max-w-7xl mx-auto">
            <p className="text-[11px] uppercase tracking-[0.2em] font-mono text-slate-500 mb-2">Admin</p>
            <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-bold text-slate-900">Operations dashboard</h1>
            <p className="text-base text-slate-600 mt-2">Manage bookings, credits, holidays and payments.</p>

            <Tabs value={tab} onValueChange={setTab} className="mt-8">
                <TabsList className="bg-slate-100 p-1 rounded-md">
                    <TabsTrigger value="overview" data-testid="admin-tab-overview" className="rounded-sm">Overview</TabsTrigger>
                    <TabsTrigger value="bookings" data-testid="admin-tab-bookings" className="rounded-sm">Bookings</TabsTrigger>
                    <TabsTrigger value="users" data-testid="admin-tab-users" className="rounded-sm">Users</TabsTrigger>
                    <TabsTrigger value="payments" data-testid="admin-tab-payments" className="rounded-sm">Payments</TabsTrigger>
                    <TabsTrigger value="holidays" data-testid="admin-tab-holidays" className="rounded-sm">Holidays</TabsTrigger>
                    <TabsTrigger value="settings" data-testid="admin-tab-settings" className="rounded-sm">Settings</TabsTrigger>
                    <TabsTrigger value="activity" data-testid="admin-tab-activity" className="rounded-sm">Activity</TabsTrigger>
                </TabsList>
                <TabsContent value="overview"><Overview /></TabsContent>
                <TabsContent value="bookings"><BookingsTab /></TabsContent>
                <TabsContent value="users"><UsersTab isSuper={isSuper} currentUserId={user?.id} /></TabsContent>
                <TabsContent value="payments"><PaymentsTab /></TabsContent>
                <TabsContent value="holidays"><HolidaysTab /></TabsContent>
                <TabsContent value="settings"><SettingsTab /></TabsContent>
                <TabsContent value="activity"><ActivityTab /></TabsContent>
            </Tabs>
        </div>
    );
}

function Overview() {
    const [stats, setStats] = useState(null);
    useEffect(() => {
        api.get("/admin/analytics").then((r) => setStats(r.data));
    }, []);
    if (!stats) return <div className="py-10 text-slate-500">Loading…</div>;
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-6">
            <Stat icon={DollarSign} label="Revenue (approved)" value={`$${stats.revenue_usd.toFixed(2)}`} />
            <Stat icon={Calendar} label="Total bookings" value={stats.total_bookings} />
            <Stat icon={Users} label="Total users" value={stats.total_users} />
            <Stat icon={BarChart3} label="Utilization (30d)" value={`${stats.utilization_30d_pct}%`} />
            <Stat icon={Plus} label="Credits sold" value={stats.credits_sold} />
            <Stat icon={Activity} label="Pending payments" value={stats.pending_purchases} />
        </div>
    );
}

function Stat({ icon: Icon, label, value }) {
    return (
        <div className="bg-white border border-slate-200 rounded-md p-5">
            <div className="flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-[0.2em] font-mono text-slate-500">{label}</span>
                <Icon className="h-4 w-4 text-slate-400" strokeWidth={1.75} />
            </div>
            <div className="mt-3 font-heading text-3xl font-bold text-slate-900 tracking-tighter">{value}</div>
        </div>
    );
}

function BookingsTab() {
    const [items, setItems] = useState([]);
    const [users, setUsers] = useState([]);
    const [overrunFor, setOverrunFor] = useState(null);
    const [credits, setCredits] = useState(1);
    const [reason, setReason] = useState("");
    const [showCreate, setShowCreate] = useState(false);
    const [newBooking, setNewBooking] = useState({ user_id: "", date: "", slot: "morning", skip_credit_deduction: false, note: "" });
    const [editFor, setEditFor] = useState(null);
    const [editForm, setEditForm] = useState({ date: "", slot: "morning" });
    const [pending, setPending] = useState([]);
    const [q, setQ] = useState("");
    const [dateFilter, setDateFilter] = useState("");
    const [sort, setSort] = useState("date_desc");

    const load = async () => {
        const [b, u, pa] = await Promise.all([
            api.get("/admin/bookings"),
            api.get("/admin/users"),
            api.get("/admin/bookings/pending-approval"),
        ]);
        setItems(b.data);
        setUsers(u.data);
        setPending(pa.data || []);
    };
    useEffect(() => { load(); }, []);
    const approvePending = async (id) => {
        try {
            await api.post(`/admin/bookings/${id}/approve`);
            toast.success("Booking approved");
            load();
        } catch (e) { toast.error("Could not approve", { description: formatApiError(e.response?.data?.detail) }); }
    };
    const rejectPending = async (id) => {
        try {
            await api.post(`/admin/bookings/${id}/reject`, { reason: "Weekly full-day limit" });
            toast.success("Booking rejected");
            load();
        } catch (e) { toast.error("Could not reject", { description: formatApiError(e.response?.data?.detail) }); }
    };
    const cancel = async (id) => {
        try {
            await api.delete(`/admin/bookings/${id}`);
            toast.success("Booking cancelled");
            load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    const submitOverrun = async () => {
        try {
            await api.post(`/admin/bookings/${overrunFor.id}/overrun`, { credits: parseInt(credits) || 1, reason });
            toast.success(`${credits} credit(s) deducted for overrun`);
            setOverrunFor(null); setCredits(1); setReason("");
            load();
        } catch (e) { toast.error("Overrun charge failed", { description: formatApiError(e.response?.data?.detail) }); }
    };
    const submitCreate = async () => {
        if (!newBooking.user_id || !newBooking.date) {
            toast.error("Pick a user and a date");
            return;
        }
        try {
            await api.post("/admin/bookings", newBooking);
            toast.success("Booking created");
            setShowCreate(false);
            setNewBooking({ user_id: "", date: "", slot: "morning", skip_credit_deduction: false, note: "" });
            load();
        } catch (e) { toast.error("Create failed", { description: formatApiError(e.response?.data?.detail) }); }
    };
    const openEdit = (b) => {
        setEditFor(b);
        setEditForm({ date: b.date, slot: b.slot });
    };
    const submitEdit = async () => {
        const body = {};
        if (editForm.date !== editFor.date) body.date = editForm.date;
        if (editForm.slot !== editFor.slot) body.slot = editForm.slot;
        if (Object.keys(body).length === 0) { toast.info("No changes"); return; }
        try {
            await api.patch(`/admin/bookings/${editFor.id}`, body);
            toast.success("Booking updated");
            setEditFor(null);
            load();
        } catch (e) { toast.error("Update failed", { description: formatApiError(e.response?.data?.detail) }); }
    };

    const visible = applySort(
        applyFilters(items, q, dateFilter, (b) => b.user_name, (b) => b.user_email, (b) => b.date),
        sort,
        (b) => b.user_name,
        (b) => b.date,
    );

    return (
        <div className="mt-6 space-y-6">
            <div className="bg-white border border-slate-200 rounded-md p-5 flex items-end gap-3 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                    <p className="font-heading text-base font-bold text-slate-900">All bookings</p>
                    <p className="text-sm text-slate-500">Create a booking on behalf of a user, edit dates/slots, charge overruns, or cancel with refund.</p>
                </div>
                <Button onClick={() => setShowCreate(!showCreate)} className="bg-brand hover:bg-brand-hover text-white rounded-md" data-testid="create-booking-toggle">
                    {showCreate ? "Close" : "+ New booking"}
                </Button>
            </div>

            {pending.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-md p-5" data-testid="pending-approval-section">
                    <div className="flex items-center justify-between gap-3 mb-3">
                        <p className="font-heading text-base font-bold text-amber-900">
                            {pending.length} booking{pending.length !== 1 ? "s" : ""} awaiting approval
                        </p>
                        <p className="text-[10px] uppercase tracking-[0.16em] font-mono text-amber-700">Weekly full-day cap exceeded</p>
                    </div>
                    <div className="space-y-2">
                        {pending.map((b) => (
                            <div key={b.id} className="bg-white border border-amber-200 rounded-md p-3 flex items-center gap-3 flex-wrap">
                                <div className="flex-1 min-w-[200px]">
                                    <div className="font-mono text-sm text-slate-900">{b.user_name} <span className="text-slate-500">·</span> <span className="text-slate-500">{b.user_email}</span></div>
                                    <div className="text-xs text-slate-600 mt-0.5">
                                        {b.date} · {b.slot.replace("_", " ")} · requested {b.credits_pending} credit{b.credits_pending !== 1 ? "s" : ""}
                                    </div>
                                </div>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => rejectPending(b.id)}
                                    data-testid={`reject-pending-${b.id}`}
                                    className="rounded-md h-8 px-3 text-xs border-red-200 text-red-700 hover:bg-red-50"
                                >
                                    Reject
                                </Button>
                                <Button
                                    size="sm"
                                    onClick={() => approvePending(b.id)}
                                    data-testid={`approve-pending-${b.id}`}
                                    className="rounded-md h-8 px-3 text-xs bg-brand hover:bg-brand-hover text-white"
                                >
                                    Approve
                                </Button>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {showCreate && (
                <div className="bg-white border border-slate-200 rounded-md p-5 space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                        <select
                            value={newBooking.user_id}
                            onChange={(e) => setNewBooking({ ...newBooking, user_id: e.target.value })}
                            data-testid="new-booking-user"
                            className="h-10 bg-slate-50 border border-slate-200 rounded-md text-sm px-3 focus:outline-none focus:ring-1 focus:ring-brand sm:col-span-2"
                        >
                            <option value="">Select user…</option>
                            {users.map((u) => (
                                <option key={u.id} value={u.id}>{u.name} — {u.email} ({u.credit_balance} cr)</option>
                            ))}
                        </select>
                        <Input type="date" value={newBooking.date} onChange={(e) => setNewBooking({ ...newBooking, date: e.target.value })} data-testid="new-booking-date" className="bg-slate-50 border-slate-200" />
                        <select
                            value={newBooking.slot}
                            onChange={(e) => setNewBooking({ ...newBooking, slot: e.target.value })}
                            data-testid="new-booking-slot"
                            className="h-10 bg-slate-50 border border-slate-200 rounded-md text-sm px-3 focus:outline-none focus:ring-1 focus:ring-brand"
                        >
                            <option value="morning">Morning · 8a–12p</option>
                            <option value="afternoon">Afternoon · 1p–5p</option>
                            <option value="full_day">Full Day</option>
                        </select>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={newBooking.skip_credit_deduction}
                                onChange={(e) => setNewBooking({ ...newBooking, skip_credit_deduction: e.target.checked })}
                                data-testid="new-booking-skip-credits"
                                className="h-4 w-4"
                            />
                            Skip credit deduction (override)
                        </label>
                        <Input placeholder="Internal note (optional)" value={newBooking.note} onChange={(e) => setNewBooking({ ...newBooking, note: e.target.value })} data-testid="new-booking-note" className="bg-slate-50 border-slate-200 sm:max-w-md" />
                        <Button onClick={submitCreate} className="bg-brand hover:bg-brand-hover text-white rounded-md" data-testid="create-booking-btn">Create booking</Button>
                    </div>
                </div>
            )}
            <TableControls q={q} setQ={setQ} date={dateFilter} setDate={setDateFilter} sort={sort} setSort={setSort} dataTestPrefix="bookings-" />
            <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200 text-left">
                        <tr>
                            <th className="px-5 py-3 text-[11px] uppercase tracking-[0.16em] font-mono text-slate-500 font-medium">Date</th>
                            <th className="px-5 py-3 text-[11px] uppercase tracking-[0.16em] font-mono text-slate-500 font-medium">Slot</th>
                            <th className="px-5 py-3 text-[11px] uppercase tracking-[0.16em] font-mono text-slate-500 font-medium">User</th>
                            <th className="px-5 py-3 text-[11px] uppercase tracking-[0.16em] font-mono text-slate-500 font-medium">Credits</th>
                            <th className="px-5 py-3 text-[11px] uppercase tracking-[0.16em] font-mono text-slate-500 font-medium">Status</th>
                            <th className="px-5 py-3"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {visible.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-500">No bookings match the filters.</td></tr>}
                        {visible.map((b) => (
                            <tr key={b.id} className="border-b border-slate-100 last:border-0">
                                <td className="px-5 py-3 text-slate-900 font-medium">{format(parseISO(b.date), "MMM d, yyyy")}</td>
                                <td className="px-5 py-3 text-slate-600 font-mono text-xs uppercase tracking-[0.14em]">{b.slot.replace("_", " ")}</td>
                                <td className="px-5 py-3 text-slate-700"><div className="font-medium">{b.user_name}</div><div className="text-xs text-slate-500">{b.user_email}</div></td>
                                <td className="px-5 py-3 text-slate-600">{b.credits_used}{b.overrun_credits ? <span className="ml-1 text-amber-600 font-mono text-xs">(+{b.overrun_credits} overrun)</span> : null}</td>
                                <td className="px-5 py-3"><StatusBadge status={b.status} /></td>
                                <td className="px-5 py-3 text-right">
                                    <div className="flex gap-1 justify-end flex-wrap">
                                        {b.status === "confirmed" && (
                                            <>
                                                <Button variant="outline" size="sm" onClick={() => openEdit(b)} data-testid={`edit-booking-${b.id}`} className="rounded-md h-8">Edit</Button>
                                                <Button variant="outline" size="sm" onClick={() => setOverrunFor(b)} data-testid={`overrun-${b.id}`} className="rounded-md h-8">Charge overrun</Button>
                                                <Button variant="ghost" size="sm" onClick={() => cancel(b.id)} data-testid={`admin-cancel-${b.id}`} className="rounded-md h-8 text-red-600 hover:bg-red-50 hover:text-red-700">Cancel</Button>
                                            </>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {overrunFor && (
                <div className="fixed inset-0 z-50 bg-slate-900/40 grid place-items-center p-4" onClick={() => setOverrunFor(null)}>
                    <div className="bg-white rounded-md border border-slate-200 p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                        <h3 className="font-heading text-xl font-bold text-slate-900">Charge overrun</h3>
                        <p className="text-sm text-slate-500 mt-1">{format(parseISO(overrunFor.date), "MMM d")} · {overrunFor.user_email} · {overrunFor.slot.replace("_"," ")}</p>
                        <p className="text-xs text-slate-500 mt-2">Per the agreement, exceeding the 15–20 min grace period may incur an additional credit deduction at admin discretion.</p>
                        <div className="mt-5 space-y-3">
                            <div>
                                <Label className="text-xs uppercase tracking-[0.16em] font-mono text-slate-600">Credits to deduct</Label>
                                <Input type="number" min={1} max={10} value={credits} onChange={(e) => setCredits(e.target.value)} data-testid="overrun-credits-input" className="mt-1.5 bg-slate-50 border-slate-200" />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-[0.16em] font-mono text-slate-600">Reason (optional)</Label>
                                <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. 45 min overrun observed" data-testid="overrun-reason-input" className="mt-1.5 bg-slate-50 border-slate-200" />
                            </div>
                        </div>
                        <div className="mt-5 flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setOverrunFor(null)} className="rounded-md">Cancel</Button>
                            <Button onClick={submitOverrun} data-testid="overrun-confirm-btn" className="bg-brand hover:bg-brand-hover text-white rounded-md">Deduct credits</Button>
                        </div>
                    </div>
                </div>
            )}

            {editFor && (
                <div className="fixed inset-0 z-50 bg-slate-900/40 grid place-items-center p-4" onClick={() => setEditFor(null)}>
                    <div className="bg-white rounded-md border border-slate-200 p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                        <h3 className="font-heading text-xl font-bold text-slate-900">Edit booking</h3>
                        <p className="text-sm text-slate-500 mt-1">{editFor.user_email}</p>
                        <div className="mt-5 space-y-3">
                            <div>
                                <Label className="text-xs uppercase tracking-[0.16em] font-mono text-slate-600">Date</Label>
                                <Input type="date" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} data-testid="edit-booking-date" className="mt-1.5 bg-slate-50 border-slate-200" />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-[0.16em] font-mono text-slate-600">Slot</Label>
                                <select
                                    value={editForm.slot}
                                    onChange={(e) => setEditForm({ ...editForm, slot: e.target.value })}
                                    data-testid="edit-booking-slot"
                                    className="mt-1.5 h-10 w-full bg-slate-50 border border-slate-200 rounded-md text-sm px-3 focus:outline-none focus:ring-1 focus:ring-brand"
                                >
                                    <option value="morning">Morning · 8a–12p</option>
                                    <option value="afternoon">Afternoon · 1p–5p</option>
                                    <option value="full_day">Full Day</option>
                                </select>
                            </div>
                        </div>
                        <div className="mt-5 flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setEditFor(null)} className="rounded-md">Cancel</Button>
                            <Button onClick={submitEdit} data-testid="edit-booking-save-btn" className="bg-brand hover:bg-brand-hover text-white rounded-md">Save changes</Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function UsersTab({ isSuper, currentUserId }) {
    const [items, setItems] = useState([]);
    const [adjustFor, setAdjustFor] = useState(null);
    const [amount, setAmount] = useState(1);
    const [reason, setReason] = useState("");
    const [editFor, setEditFor] = useState(null); // user being edited
    const [editForm, setEditForm] = useState({ name: "", email: "", password: "", role: "user", no_show_count: 0 });
    const [showCreate, setShowCreate] = useState(false);
    const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "user", initial_credits: 0 });
    const [q, setQ] = useState("");
    const [dateFilter, setDateFilter] = useState("");
    const [sort, setSort] = useState("date_desc");

    const load = () => api.get("/admin/users").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const adjust = async () => {
        try {
            await api.post("/admin/credits/adjust", { user_id: adjustFor.id, amount: parseInt(amount), reason });
            toast.success("Credits adjusted");
            setAdjustFor(null); setAmount(1); setReason("");
            load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const createUser = async () => {
        try {
            await api.post("/admin/users", {
                name: newUser.name,
                email: newUser.email,
                password: newUser.password,
                role: newUser.role,
                initial_credits: parseInt(newUser.initial_credits) || 0,
            });
            toast.success(`${newUser.role.replace("_", " ")} account created`);
            setShowCreate(false);
            setNewUser({ name: "", email: "", password: "", role: "user", initial_credits: 0 });
            load();
        } catch (e) { toast.error("Create failed", { description: formatApiError(e.response?.data?.detail) }); }
    };

    const openEdit = (u) => {
        setEditFor(u);
        setEditForm({ name: u.name || "", email: u.email, password: "", role: u.role, no_show_count: u.no_show_count || 0 });
    };

    const saveEdit = async () => {
        const body = {};
        if (editForm.name !== editFor.name) body.name = editForm.name;
        if (editForm.email !== editFor.email) body.email = editForm.email;
        if (editForm.password) body.password = editForm.password;
        if (isSuper && editForm.role !== editFor.role) body.role = editForm.role;
        if (parseInt(editForm.no_show_count) !== editFor.no_show_count) body.no_show_count = parseInt(editForm.no_show_count);
        if (Object.keys(body).length === 0) {
            toast.info("No changes");
            return;
        }
        try {
            await api.patch(`/admin/users/${editFor.id}`, body);
            toast.success("User updated");
            setEditFor(null);
            load();
        } catch (e) { toast.error("Update failed", { description: formatApiError(e.response?.data?.detail) }); }
    };

    const deleteUser = async (u) => {
        if (!window.confirm(`Permanently delete ${u.email}? This cancels their bookings and removes their credits.`)) return;
        try {
            await api.delete(`/admin/users/${u.id}`);
            toast.success("User deleted");
            load();
        } catch (e) { toast.error("Delete failed", { description: formatApiError(e.response?.data?.detail) }); }
    };

    return (
        <div className="mt-6 space-y-6">
            <div className="bg-white border border-slate-200 rounded-md p-5 flex items-end gap-3 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                    <p className="font-heading text-base font-bold text-slate-900">User accounts</p>
                    <p className="text-sm text-slate-500">Create, edit and remove user accounts. {isSuper && "Super admins can also manage admin and super-admin roles."}</p>
                </div>
                <Button onClick={() => setShowCreate(!showCreate)} className="bg-brand hover:bg-brand-hover text-white rounded-md" data-testid="create-user-toggle">
                    {showCreate ? "Close" : "+ New account"}
                </Button>
            </div>
            {showCreate && (
                <div className="bg-white border border-slate-200 rounded-md p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                    <Input placeholder="Full name" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} data-testid="new-user-name" className="bg-slate-50 border-slate-200" />
                    <Input placeholder="Email" type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} data-testid="new-user-email" className="bg-slate-50 border-slate-200" />
                    <Input placeholder="Password (≥6)" type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} data-testid="new-user-password" className="bg-slate-50 border-slate-200" />
                    <select
                        value={newUser.role}
                        onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                        data-testid="new-user-role"
                        className="h-10 bg-slate-50 border border-slate-200 rounded-md text-sm px-3 focus:outline-none focus:ring-1 focus:ring-brand"
                    >
                        <option value="user">User</option>
                        {isSuper && <option value="admin">Admin</option>}
                        {isSuper && <option value="super_admin">Super Admin</option>}
                    </select>
                    <div className="flex gap-2">
                        <Input placeholder="Credits" type="number" min={0} value={newUser.initial_credits} onChange={(e) => setNewUser({ ...newUser, initial_credits: e.target.value })} data-testid="new-user-credits" className="bg-slate-50 border-slate-200 flex-1" />
                        <Button onClick={createUser} className="bg-brand hover:bg-brand-hover text-white rounded-md" data-testid="create-user-btn">Create</Button>
                    </div>
                </div>
            )}
            <PasswordResetsCard />
            <TableControls
                q={q} setQ={setQ}
                date={dateFilter} setDate={setDateFilter}
                sort={sort} setSort={setSort}
                sortOptions={[
                    { value: "date_desc", label: "Created · newest" },
                    { value: "date_asc", label: "Created · oldest" },
                    { value: "name_asc", label: "Name · A → Z" },
                    { value: "name_desc", label: "Name · Z → A" },
                ]}
                dataTestPrefix="users-"
            />
            <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200 text-left">
                        <tr>
                            <th className="px-5 py-3 text-[11px] uppercase tracking-[0.16em] font-mono text-slate-500 font-medium">Name</th>
                            <th className="px-5 py-3 text-[11px] uppercase tracking-[0.16em] font-mono text-slate-500 font-medium">Email</th>
                            <th className="px-5 py-3 text-[11px] uppercase tracking-[0.16em] font-mono text-slate-500 font-medium">Role</th>
                            <th className="px-5 py-3 text-[11px] uppercase tracking-[0.16em] font-mono text-slate-500 font-medium">Credits</th>
                            <th className="px-5 py-3 text-[11px] uppercase tracking-[0.16em] font-mono text-slate-500 font-medium">No-shows</th>
                            <th className="px-5 py-3"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {(() => {
                            const usersVisible = applySort(
                                applyFilters(items, q, dateFilter, (u) => u.name, (u) => u.email, (u) => u.created_at?.slice(0, 10)),
                                sort,
                                (u) => u.name,
                                (u) => u.created_at,
                            );
                            if (usersVisible.length === 0) return <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-500">No users match the filters.</td></tr>;
                            return usersVisible.map((u) => {
                            const canEdit = isSuper || u.role === "user";
                            const canDelete = canEdit && u.id !== currentUserId;
                            return (
                                <tr key={u.id} className="border-b border-slate-100 last:border-0">
                                    <td className="px-5 py-3 font-medium text-slate-900">{u.name}</td>
                                    <td className="px-5 py-3 text-slate-600">{u.email}</td>
                                    <td className="px-5 py-3">
                                        <span className="font-mono text-xs uppercase tracking-[0.14em] text-slate-700">{u.role.replace("_", " ")}</span>
                                    </td>
                                    <td className="px-5 py-3 text-slate-700 font-mono">{u.credit_balance}</td>
                                    <td className="px-5 py-3 text-slate-700">{u.no_show_count}</td>
                                    <td className="px-5 py-3 text-right">
                                        <div className="flex gap-1 justify-end flex-wrap">
                                            <Button size="sm" variant="outline" onClick={() => setAdjustFor(u)} data-testid={`adjust-${u.id}`} className="rounded-md h-8">Adjust credits</Button>
                                            {canEdit && <Button size="sm" variant="outline" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`} className="rounded-md h-8">Edit</Button>}
                                            {canDelete && <Button size="sm" variant="ghost" onClick={() => deleteUser(u)} data-testid={`delete-user-${u.id}`} className="rounded-md h-8 text-red-600 hover:bg-red-50 hover:text-red-700">Delete</Button>}
                                        </div>
                                    </td>
                                </tr>
                            );
                        });
                        })()}
                    </tbody>
                </table>
            </div>

            {adjustFor && (
                <div className="fixed inset-0 z-50 bg-slate-900/40 grid place-items-center p-4" onClick={() => setAdjustFor(null)}>
                    <div className="bg-white rounded-md border border-slate-200 p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                        <h3 className="font-heading text-xl font-bold text-slate-900">Adjust credits — {adjustFor.name}</h3>
                        <p className="text-sm text-slate-500 mt-1">Positive amount grants credits; negative revokes.</p>
                        <div className="mt-5 space-y-3">
                            <div>
                                <Label className="text-xs uppercase tracking-[0.16em] font-mono text-slate-600">Amount</Label>
                                <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="adjust-amount" className="mt-1.5 bg-slate-50 border-slate-200" />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-[0.16em] font-mono text-slate-600">Reason</Label>
                                <Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="adjust-reason" className="mt-1.5 bg-slate-50 border-slate-200" />
                            </div>
                        </div>
                        <div className="mt-5 flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setAdjustFor(null)} className="rounded-md">Cancel</Button>
                            <Button onClick={adjust} data-testid="adjust-confirm" className="bg-brand hover:bg-brand-hover text-white rounded-md">Apply</Button>
                        </div>
                    </div>
                </div>
            )}

            {editFor && (
                <div className="fixed inset-0 z-50 bg-slate-900/40 grid place-items-center p-4" onClick={() => setEditFor(null)}>
                    <div className="bg-white rounded-md border border-slate-200 p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                        <h3 className="font-heading text-xl font-bold text-slate-900">Edit user</h3>
                        <p className="text-sm text-slate-500 mt-1">{editFor.email}</p>
                        <div className="mt-5 space-y-3">
                            <div>
                                <Label className="text-xs uppercase tracking-[0.16em] font-mono text-slate-600">Name</Label>
                                <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-user-name" className="mt-1.5 bg-slate-50 border-slate-200" />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-[0.16em] font-mono text-slate-600">Email</Label>
                                <Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} data-testid="edit-user-email" className="mt-1.5 bg-slate-50 border-slate-200" />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-[0.16em] font-mono text-slate-600">New password (optional)</Label>
                                <Input type="password" placeholder="Leave blank to keep" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} data-testid="edit-user-password" className="mt-1.5 bg-slate-50 border-slate-200" />
                            </div>
                            {isSuper && (
                                <div>
                                    <Label className="text-xs uppercase tracking-[0.16em] font-mono text-slate-600">Role</Label>
                                    <select
                                        value={editForm.role}
                                        onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                                        data-testid="edit-user-role"
                                        className="mt-1.5 h-10 w-full bg-slate-50 border border-slate-200 rounded-md text-sm px-3 focus:outline-none focus:ring-1 focus:ring-brand"
                                    >
                                        <option value="user">User</option>
                                        <option value="admin">Admin</option>
                                        <option value="super_admin">Super Admin</option>
                                    </select>
                                </div>
                            )}
                            <div>
                                <Label className="text-xs uppercase tracking-[0.16em] font-mono text-slate-600">No-show count</Label>
                                <Input type="number" min={0} value={editForm.no_show_count} onChange={(e) => setEditForm({ ...editForm, no_show_count: e.target.value })} data-testid="edit-user-no-show" className="mt-1.5 bg-slate-50 border-slate-200" />
                            </div>
                        </div>
                        <div className="mt-5 flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setEditFor(null)} className="rounded-md">Cancel</Button>
                            <Button onClick={saveEdit} data-testid="edit-user-save-btn" className="bg-brand hover:bg-brand-hover text-white rounded-md">Save changes</Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function PaymentsTab() {
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [dateFilter, setDateFilter] = useState("");
    const [sort, setSort] = useState("date_desc");
    const load = () => api.get("/admin/purchases").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);
    const approve = async (id) => { try { await api.post(`/admin/purchases/${id}/approve`); toast.success("Approved"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
    const reject = async (id) => { try { await api.post(`/admin/purchases/${id}/reject`); toast.success("Rejected"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
    const visible = applySort(
        applyFilters(items, q, dateFilter, (p) => p.user_name, (p) => p.user_email, (p) => p.created_at?.slice(0, 10)),
        sort,
        (p) => p.user_name,
        (p) => p.created_at,
    );
    return (
        <div className="mt-6">
            <TableControls
                q={q} setQ={setQ}
                date={dateFilter} setDate={setDateFilter}
                sort={sort} setSort={setSort}
                sortOptions={[
                    { value: "date_desc", label: "Date · newest" },
                    { value: "date_asc", label: "Date · oldest" },
                    { value: "name_asc", label: "Name · A → Z" },
                    { value: "name_desc", label: "Name · Z → A" },
                ]}
                dataTestPrefix="payments-"
            />
            <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
                <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200 text-left">
                    <tr>
                        <th className="px-5 py-3 text-[11px] uppercase tracking-[0.16em] font-mono text-slate-500 font-medium">Date</th>
                        <th className="px-5 py-3 text-[11px] uppercase tracking-[0.16em] font-mono text-slate-500 font-medium">User</th>
                        <th className="px-5 py-3 text-[11px] uppercase tracking-[0.16em] font-mono text-slate-500 font-medium">Package</th>
                        <th className="px-5 py-3 text-[11px] uppercase tracking-[0.16em] font-mono text-slate-500 font-medium">Amount</th>
                        <th className="px-5 py-3 text-[11px] uppercase tracking-[0.16em] font-mono text-slate-500 font-medium">PayPal Ref</th>
                        <th className="px-5 py-3 text-[11px] uppercase tracking-[0.16em] font-mono text-slate-500 font-medium">Status</th>
                        <th className="px-5 py-3"></th>
                    </tr>
                </thead>
                <tbody>
                    {visible.length === 0 && <tr><td colSpan={7} className="px-5 py-10 text-center text-slate-500">No purchases match the filters.</td></tr>}
                    {visible.map((p) => (
                        <tr key={p.id} className="border-b border-slate-100 last:border-0">
                            <td className="px-5 py-3 text-slate-700">{format(parseISO(p.created_at), "MMM d, HH:mm")}</td>
                            <td className="px-5 py-3 text-slate-700"><div className="font-medium">{p.user_name}</div><div className="text-xs text-slate-500">{p.user_email}</div></td>
                            <td className="px-5 py-3 text-slate-700">{p.package} × {p.quantity} ({p.credits} cr)</td>
                            <td className="px-5 py-3 font-mono text-slate-900">${p.amount_usd.toFixed(2)}</td>
                            <td className="px-5 py-3 text-xs font-mono text-slate-700">{p.paypal_reference || "—"}</td>
                            <td className="px-5 py-3"><PurchaseBadge status={p.status} /></td>
                            <td className="px-5 py-3 text-right">
                                {(p.status === "submitted" || p.status === "pending_payment") && (
                                    <div className="flex gap-1 justify-end">
                                        <Button size="sm" onClick={() => approve(p.id)} data-testid={`approve-${p.id}`} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-md h-8">
                                            <Check className="h-3.5 w-3.5 mr-1" /> Approve
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={() => reject(p.id)} data-testid={`reject-${p.id}`} className="rounded-md h-8">
                                            <X className="h-3.5 w-3.5 mr-1" /> Reject
                                        </Button>
                                    </div>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            </div>
        </div>
    );
}

function HolidaysTab() {
    const [holidays, setHolidays] = useState([]);
    const [blocks, setBlocks] = useState([]);
    const [hDate, setHDate] = useState("");
    const [hName, setHName] = useState("");
    const [bDate, setBDate] = useState("");
    const [bReason, setBReason] = useState("");

    const load = async () => {
        const [h, b] = await Promise.all([api.get("/holidays"), api.get("/admin/blocked-dates")]);
        setHolidays(h.data); setBlocks(b.data);
    };
    useEffect(() => { load(); }, []);

    const addHoliday = async () => {
        try { await api.post("/holidays", { date: hDate, name: hName }); toast.success("Holiday added"); setHDate(""); setHName(""); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    const delHoliday = async (id) => { try { await api.delete(`/holidays/${id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
    const addBlock = async () => {
        try { await api.post("/admin/blocked-dates", { date: bDate, name: bReason || "Blocked" }); toast.success("Date blocked"); setBDate(""); setBReason(""); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    const delBlock = async (id) => { try { await api.delete(`/admin/blocked-dates/${id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            <div className="bg-white border border-slate-200 rounded-md p-6">
                <h3 className="font-heading text-lg font-bold text-slate-900">Public holidays</h3>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <Input type="date" value={hDate} onChange={(e) => setHDate(e.target.value)} data-testid="holiday-date" className="bg-slate-50 border-slate-200" />
                    <Input placeholder="Name (e.g. Independence Day)" value={hName} onChange={(e) => setHName(e.target.value)} data-testid="holiday-name" className="bg-slate-50 border-slate-200 col-span-1 sm:col-span-2" />
                    <Button onClick={addHoliday} className="bg-brand hover:bg-brand-hover text-white rounded-md sm:col-span-3" data-testid="add-holiday-btn">
                        <Plus className="h-4 w-4 mr-1.5" /> Add holiday
                    </Button>
                </div>
                <div className="mt-5 divide-y divide-slate-100">
                    {holidays.length === 0 && <div className="text-sm text-slate-500 py-4">No holidays defined.</div>}
                    {holidays.map((h) => (
                        <div key={h.id} className="flex items-center justify-between py-3">
                            <div>
                                <div className="font-medium text-slate-900">{format(parseISO(h.date), "MMM d, yyyy")}</div>
                                <div className="text-xs text-slate-500">{h.name}</div>
                            </div>
                            <Button variant="ghost" size="icon" onClick={() => delHoliday(h.id)} data-testid={`del-holiday-${h.id}`} className="h-8 w-8 text-slate-400 hover:text-red-600">
                                <Trash2 className="h-4 w-4" strokeWidth={1.75} />
                            </Button>
                        </div>
                    ))}
                </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-md p-6">
                <h3 className="font-heading text-lg font-bold text-slate-900">Blocked dates</h3>
                <p className="text-sm text-slate-500">One-off closures (training, maintenance, etc.).</p>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <Input type="date" value={bDate} onChange={(e) => setBDate(e.target.value)} data-testid="block-date" className="bg-slate-50 border-slate-200" />
                    <Input placeholder="Reason" value={bReason} onChange={(e) => setBReason(e.target.value)} data-testid="block-reason" className="bg-slate-50 border-slate-200 col-span-1 sm:col-span-2" />
                    <Button onClick={addBlock} className="bg-brand hover:bg-brand-hover text-white rounded-md sm:col-span-3" data-testid="add-block-btn">
                        <Plus className="h-4 w-4 mr-1.5" /> Block date
                    </Button>
                </div>
                <div className="mt-5 divide-y divide-slate-100">
                    {blocks.length === 0 && <div className="text-sm text-slate-500 py-4">No blocked dates.</div>}
                    {blocks.map((b) => (
                        <div key={b.id} className="flex items-center justify-between py-3">
                            <div>
                                <div className="font-medium text-slate-900">{format(parseISO(b.date), "MMM d, yyyy")}</div>
                                <div className="text-xs text-slate-500">{b.reason}</div>
                            </div>
                            <Button variant="ghost" size="icon" onClick={() => delBlock(b.id)} className="h-8 w-8 text-slate-400 hover:text-red-600">
                                <Trash2 className="h-4 w-4" strokeWidth={1.75} />
                            </Button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function ActivityTab() {
    const [items, setItems] = useState([]);
    useEffect(() => { api.get("/admin/activity").then((r) => setItems(r.data)); }, []);
    return (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden mt-6">
            <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200 text-left">
                    <tr>
                        <th className="px-5 py-3 text-[11px] uppercase tracking-[0.16em] font-mono text-slate-500 font-medium">When</th>
                        <th className="px-5 py-3 text-[11px] uppercase tracking-[0.16em] font-mono text-slate-500 font-medium">Actor</th>
                        <th className="px-5 py-3 text-[11px] uppercase tracking-[0.16em] font-mono text-slate-500 font-medium">Action</th>
                        <th className="px-5 py-3 text-[11px] uppercase tracking-[0.16em] font-mono text-slate-500 font-medium">Meta</th>
                    </tr>
                </thead>
                <tbody>
                    {items.length === 0 && <tr><td colSpan={4} className="px-5 py-10 text-center text-slate-500">No activity yet.</td></tr>}
                    {items.map((a) => (
                        <tr key={a.id} className="border-b border-slate-100 last:border-0">
                            <td className="px-5 py-3 text-slate-700 font-mono text-xs">{format(parseISO(a.created_at), "MMM d, HH:mm:ss")}</td>
                            <td className="px-5 py-3 text-slate-700">{a.actor_email || "—"}</td>
                            <td className="px-5 py-3 text-slate-900 font-medium">{a.action}</td>
                            <td className="px-5 py-3 text-xs font-mono text-slate-500">{Object.keys(a.meta).length ? JSON.stringify(a.meta) : "—"}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function SettingsTab() {
    const [settings, setSettings] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/admin/settings").then((r) => setSettings(r.data));
    }, []);

    const updatePackage = (key, field, value) => {
        setSettings((s) => ({
            ...s,
            packages: { ...s.packages, [key]: { ...s.packages[key], [field]: value } },
        }));
    };
    const updateLimit = (field, value) => {
        setSettings((s) => ({ ...s, limits: { ...s.limits, [field]: value } }));
    };

    const save = async () => {
        setSaving(true);
        try {
            // coerce numeric fields
            const packages = Object.fromEntries(
                Object.entries(settings.packages).map(([k, v]) => [
                    k,
                    { credits: parseInt(v.credits) || 1, price_usd: parseFloat(v.price_usd) || 0, label: v.label },
                ])
            );
            const limits = {
                ...settings.limits,
                credit_expiry_days: parseInt(settings.limits.credit_expiry_days) || 45,
                max_purchase_per_30_days: parseInt(settings.limits.max_purchase_per_30_days) || 20,
                max_use_per_14_days: parseInt(settings.limits.max_use_per_14_days) || 12,
                booking_window_days: parseInt(settings.limits.booking_window_days) || 14,
            };
            await api.put("/admin/settings", { packages, limits });
            toast.success("Settings saved");
        } catch (e) {
            toast.error("Save failed", { description: formatApiError(e.response?.data?.detail) || e.message });
        } finally {
            setSaving(false);
        }
    };

    if (!settings) return <div className="py-10 text-slate-500">Loading…</div>;

    return (
        <div className="mt-6 space-y-6">
            <div className="bg-white border border-slate-200 rounded-md p-6 md:p-8">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <div className="flex items-center gap-2">
                            <SettingsIcon className="h-5 w-5 text-slate-700" strokeWidth={1.75} />
                            <h2 className="font-heading text-xl font-bold text-slate-900">Pricing & limits</h2>
                        </div>
                        <p className="text-sm text-slate-500 mt-1">Edit packages, prices, and business rule limits. Changes apply to new purchases and bookings immediately.</p>
                    </div>
                    <Button onClick={save} disabled={saving} data-testid="settings-save-btn" className="bg-brand hover:bg-brand-hover text-white rounded-md">
                        <Save className="h-4 w-4 mr-1.5" strokeWidth={1.75} />
                        {saving ? "Saving…" : "Save changes"}
                    </Button>
                </div>

                <h3 className="font-heading text-base font-bold text-slate-900 mt-7 pb-2 border-b border-slate-100">Packages</h3>
                <div className="overflow-x-auto mt-4">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left">
                                <th className="px-3 py-2 text-[11px] uppercase tracking-[0.16em] font-mono text-slate-500 font-medium">Key</th>
                                <th className="px-3 py-2 text-[11px] uppercase tracking-[0.16em] font-mono text-slate-500 font-medium">Label</th>
                                <th className="px-3 py-2 text-[11px] uppercase tracking-[0.16em] font-mono text-slate-500 font-medium">Credits</th>
                                <th className="px-3 py-2 text-[11px] uppercase tracking-[0.16em] font-mono text-slate-500 font-medium">Price (USD)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Object.entries(settings.packages).map(([key, p]) => (
                                <tr key={key} className="border-t border-slate-100">
                                    <td className="px-3 py-2 font-mono text-xs text-slate-700">{key}</td>
                                    <td className="px-3 py-2">
                                        <Input value={p.label} onChange={(e) => updatePackage(key, "label", e.target.value)} data-testid={`pkg-${key}-label`} className="h-9 bg-slate-50 border-slate-200" />
                                    </td>
                                    <td className="px-3 py-2 w-28">
                                        <Input type="number" min={1} value={p.credits} onChange={(e) => updatePackage(key, "credits", e.target.value)} data-testid={`pkg-${key}-credits`} className="h-9 bg-slate-50 border-slate-200" />
                                    </td>
                                    <td className="px-3 py-2 w-32">
                                        <Input type="number" min={0} step="0.01" value={p.price_usd} onChange={(e) => updatePackage(key, "price_usd", e.target.value)} data-testid={`pkg-${key}-price`} className="h-9 bg-slate-50 border-slate-200" />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <h3 className="font-heading text-base font-bold text-slate-900 mt-8 pb-2 border-b border-slate-100">Business rules</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-5">
                    <Field label="Credit expiry (days)" tip="Credits become unusable this many days after purchase.">
                        <Input type="number" min={1} value={settings.limits.credit_expiry_days} onChange={(e) => updateLimit("credit_expiry_days", e.target.value)} data-testid="limit-expiry" className="h-10 bg-slate-50 border-slate-200" />
                    </Field>
                    <Field label="Booking window (days in advance)" tip="How far ahead users may book.">
                        <Input type="number" min={1} value={settings.limits.booking_window_days} onChange={(e) => updateLimit("booking_window_days", e.target.value)} data-testid="limit-window" className="h-10 bg-slate-50 border-slate-200" />
                    </Field>
                    <Field label="Max purchase / 30 days" tip="Cap on credits a user can buy in a rolling 30-day window.">
                        <Input type="number" min={1} value={settings.limits.max_purchase_per_30_days} onChange={(e) => updateLimit("max_purchase_per_30_days", e.target.value)} data-testid="limit-purchase" className="h-10 bg-slate-50 border-slate-200" />
                    </Field>
                    <Field label="Max use / 14 days" tip="Cap on credits a user can spend in a rolling 14-day window.">
                        <Input type="number" min={1} value={settings.limits.max_use_per_14_days} onChange={(e) => updateLimit("max_use_per_14_days", e.target.value)} data-testid="limit-use" className="h-10 bg-slate-50 border-slate-200" />
                    </Field>
                </div>

                <h3 className="font-heading text-base font-bold text-slate-900 mt-8 pb-2 border-b border-slate-100">PayPal</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-5">
                    <Field label="PayPal handle" tip="Shown to users on the Buy Credits page.">
                        <Input value={settings.limits.paypal_handle} onChange={(e) => updateLimit("paypal_handle", e.target.value)} data-testid="limit-paypal-handle" className="h-10 bg-slate-50 border-slate-200" />
                    </Field>
                    <Field label="PayPal QR image URL" tip="Path or URL to your PayPal QR. Default points to /assets/paypal-qr.jpg.">
                        <Input value={settings.limits.paypal_qr_url} onChange={(e) => updateLimit("paypal_qr_url", e.target.value)} data-testid="limit-paypal-qr" className="h-10 bg-slate-50 border-slate-200 font-mono text-xs" />
                    </Field>
                </div>
                <div className="mt-5 p-4 bg-slate-50 border border-slate-200 rounded-md">
                    <p className="text-[11px] uppercase tracking-[0.18em] font-mono text-slate-500 mb-2">QR preview</p>
                    <img src={settings.limits.paypal_qr_url} alt="PayPal QR" className="h-40 w-40 object-contain bg-white border border-slate-200 rounded-sm" />
                </div>
            </div>
            <TermsVersionCard />
        </div>
    );
}

function Field({ label, tip, children }) {
    return (
        <div>
            <Label className="text-xs uppercase tracking-[0.16em] font-mono text-slate-600">{label}</Label>
            <div className="mt-1.5">{children}</div>
            {tip && <p className="text-xs text-slate-500 mt-1.5">{tip}</p>}
        </div>
    );
}

function TermsVersionCard() {
    const [version, setVersion] = useState(null);
    const [acceptances, setAcceptances] = useState([]);
    const [showBump, setShowBump] = useState(false);
    const [notes, setNotes] = useState("");
    const [busy, setBusy] = useState(false);

    const load = async () => {
        const [v, a] = await Promise.all([
            api.get("/terms/version"),
            api.get("/admin/terms/acceptances"),
        ]);
        setVersion(v.data);
        setAcceptances(a.data);
    };
    useEffect(() => { load(); }, []);

    const bump = async () => {
        if (!window.confirm("Publish a new terms version? All users will be re-prompted to accept on their next page load.")) return;
        setBusy(true);
        try {
            await api.post("/admin/terms/bump", { notes: notes.trim() || null });
            toast.success("New terms version published", { description: "All users will be re-prompted on next visit." });
            setShowBump(false);
            setNotes("");
            await load();
        } catch (e) {
            toast.error("Bump failed", { description: formatApiError(e.response?.data?.detail) });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="bg-white border border-slate-200 rounded-md p-6 md:p-8">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <div className="flex items-center gap-2">
                        <FileText className="h-5 w-5 text-slate-700" strokeWidth={1.75} />
                        <h2 className="font-heading text-xl font-bold text-slate-900">Terms version</h2>
                    </div>
                    <p className="text-sm text-slate-500 mt-1 max-w-xl">
                        Bumping the version forces every user to re-read and accept the latest agreement before they can continue.
                        Use this whenever you edit the contract on the <span className="font-mono">/terms</span> page.
                    </p>
                </div>
                <Button onClick={() => setShowBump(!showBump)} data-testid="terms-bump-toggle" className="bg-slate-900 hover:bg-slate-800 text-white rounded-md">
                    {showBump ? "Cancel" : "Publish new version"}
                </Button>
            </div>

            <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-slate-50 border border-slate-200 rounded-md p-4">
                    <p className="text-[11px] uppercase tracking-[0.18em] font-mono text-slate-500">Current version</p>
                    <p className="font-heading text-base font-bold text-slate-900 mt-1.5">
                        {version ? format(parseISO(version.version), "MMM d, yyyy") : "—"}
                    </p>
                    <p className="text-xs font-mono text-slate-500 mt-0.5">
                        {version ? format(parseISO(version.version), "HH:mm 'UTC'") : ""}
                    </p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-md p-4">
                    <p className="text-[11px] uppercase tracking-[0.18em] font-mono text-slate-500">Notes</p>
                    <p className="text-sm text-slate-700 mt-1.5 leading-relaxed">{version?.notes || "—"}</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-md p-4">
                    <p className="text-[11px] uppercase tracking-[0.18em] font-mono text-slate-500">Total acceptances</p>
                    <p className="font-heading text-2xl font-bold text-slate-900 mt-1.5 tracking-tighter">{acceptances.length}</p>
                    <p className="text-xs text-slate-500 mt-0.5">All-time across all versions.</p>
                </div>
            </div>

            {showBump && (
                <div className="mt-5 p-4 bg-amber-50 border border-amber-200 rounded-md">
                    <div className="flex items-start gap-3">
                        <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" strokeWidth={1.75} />
                        <div className="flex-1">
                            <p className="font-heading font-semibold text-slate-900">Publish a new version</p>
                            <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                                Edit the <span className="font-mono">/terms</span> page first, then publish here.
                                Every existing user will see the re-acceptance modal on their next page load.
                            </p>
                        </div>
                    </div>
                    <div className="mt-3">
                        <Label className="text-xs uppercase tracking-[0.16em] font-mono text-slate-600">Notes (optional)</Label>
                        <Input
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder='e.g. "Updated overrun policy and added parking clause"'
                            data-testid="terms-bump-notes"
                            className="mt-1.5 bg-white border-slate-200"
                        />
                        <p className="text-xs text-slate-500 mt-1.5">Shown to users in the re-acceptance dialog.</p>
                    </div>
                    <div className="mt-4 flex justify-end">
                        <Button onClick={bump} disabled={busy} data-testid="terms-bump-confirm" className="bg-amber-600 hover:bg-amber-700 text-white rounded-md">
                            {busy ? "Publishing…" : "Publish new version"}
                        </Button>
                    </div>
                </div>
            )}

            <h3 className="font-heading text-base font-bold text-slate-900 mt-8 pb-2 border-b border-slate-100">Recent acceptances</h3>
            <div className="mt-3 divide-y divide-slate-100 max-h-64 overflow-y-auto">
                {acceptances.length === 0 && <p className="text-sm text-slate-500 py-4">No acceptances logged yet.</p>}
                {acceptances.slice(0, 50).map((a) => (
                    <div key={a.id} className="flex items-center justify-between py-2.5 text-sm">
                        <div>
                            <span className="text-slate-900 font-medium">{a.email}</span>
                            <span className="text-xs font-mono text-slate-500 ml-2">v{format(parseISO(a.version), "yyyy-MM-dd HH:mm")}</span>
                        </div>
                        <span className="text-xs font-mono text-slate-500">{format(parseISO(a.accepted_at), "MMM d, HH:mm")}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}


function PasswordResetsCard() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const r = await api.get("/admin/password-resets");
            setItems(r.data);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const buildLink = (token) => `${window.location.origin}/reset-password?token=${token}`;

    const copy = async (token) => {
        try {
            await navigator.clipboard.writeText(buildLink(token));
            toast.success("Reset link copied to clipboard");
        } catch (_) {
            toast.error("Could not copy");
        }
    };

    const revoke = async (token) => {
        try {
            await api.post(`/admin/password-resets/${token}/revoke`);
            toast.success("Reset link revoked");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    return (
        <div className="bg-white border border-slate-200 rounded-md p-5">
            <div className="flex items-center gap-2">
                <KeyRound className="h-4 w-4 text-slate-700" strokeWidth={1.75} />
                <p className="font-heading text-base font-bold text-slate-900">Password reset requests</p>
            </div>
            <p className="text-sm text-slate-500 mt-1">Email is disabled — share these links manually with the user (WhatsApp, SMS, in person). Links expire 1 hour after request.</p>
            {loading ? (
                <p className="text-sm text-slate-500 mt-4">Loading…</p>
            ) : items.length === 0 ? (
                <p className="text-sm text-slate-500 mt-4 py-2">No active reset requests.</p>
            ) : (
                <div className="mt-4 divide-y divide-slate-100">
                    {items.map((r) => (
                        <div key={r.token} className="py-3 flex items-center justify-between gap-3 flex-wrap">
                            <div className="min-w-0 flex-1">
                                <div className="font-medium text-slate-900 truncate">{r.email}</div>
                                <div className="text-xs text-slate-500 truncate font-mono">{buildLink(r.token)}</div>
                                <div className="text-[10px] uppercase tracking-[0.14em] font-mono text-slate-400 mt-0.5">requested {format(parseISO(r.created_at), "MMM d, HH:mm")} · expires {format(parseISO(r.expires_at), "HH:mm")}</div>
                            </div>
                            <div className="flex gap-2">
                                <Button variant="outline" size="sm" onClick={() => copy(r.token)} data-testid={`copy-reset-${r.token.slice(0,8)}`} className="rounded-md h-8">
                                    <Copy className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.75} /> Copy link
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => revoke(r.token)} className="rounded-md h-8 text-slate-500 hover:text-red-600">Revoke</Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}




function StatusBadge({ status }) {
    const map = {
        confirmed: "bg-emerald-50 text-emerald-700 border-emerald-200",
        cancelled: "bg-slate-50 text-slate-500 border-slate-200",
        completed: "bg-slate-100 text-slate-700 border-slate-200",
        no_show: "bg-red-50 text-red-700 border-red-200",
        overrun_charged: "bg-amber-50 text-amber-700 border-amber-200",
    };
    return <span className={`inline-flex px-2 py-0.5 text-[10px] uppercase tracking-[0.14em] font-mono border rounded-sm ${map[status] || "bg-slate-50 text-slate-700"}`}>{status.replace("_", " ")}</span>;
}

function PurchaseBadge({ status }) {
    const map = {
        pending_payment: "bg-amber-50 text-amber-700 border-amber-200",
        submitted: "bg-blue-50 text-blue-700 border-blue-200",
        approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
        rejected: "bg-red-50 text-red-700 border-red-200",
        cancelled: "bg-slate-100 text-slate-600 border-slate-200",
        refunded: "bg-slate-50 text-slate-700 border-slate-200",
    };
    return <span className={`inline-flex px-2 py-0.5 text-[10px] uppercase tracking-[0.14em] font-mono border rounded-sm ${map[status] || "bg-slate-50 text-slate-700"}`}>{status.replace("_", " ")}</span>;
}
